import React from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { SubsidyRequest } from '@trnsact/trnsact-shared-types';

interface Props {
  deals: SubsidyRequest[];
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

export const SubsidyLedgerTable = ({ deals, total, page, rowsPerPage, onPageChange }: Props) => {
  const pageFinanceTotal = deals.reduce((sum, d) => sum + (d.financeAmount ?? 0), 0);

  return (
    <Box mt={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" color="text.secondary">
          Subsidy deal ledger —{' '}
          <Typography component="span" variant="subtitle2" fontWeight={700}>
            {total} records
          </Typography>
        </Typography>
      </Stack>

      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>CUSTOMER</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>DEALER</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>LENDER</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>EQUIPMENT</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">
                FINANCE AMT
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">
                DNET
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">
                TERM
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">
                SUBSIDY %
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">
                SUBSIDY AMT
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {deals.map(deal => {
              const equipmentLabel =
                [deal.equipment?.year, deal.equipment?.make, deal.equipment?.model].filter(Boolean).join(' ') || '—';

              return (
                <TableRow key={deal.subsidyRequestId} hover>
                  <TableCell>
                    <Typography variant="caption" fontWeight={600}>
                      {deal.customer}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption">{deal.dealer}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption">{deal.lender}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" fontWeight={600}>
                      {equipmentLabel}
                    </Typography>
                    {deal.equipment?.serialNumberVin && (
                      <>
                        <br />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          S/N {deal.equipment.serialNumberVin}
                        </Typography>
                      </>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="caption" fontWeight={600}>
                      {formatCurrency(deal.financeAmount ?? 0)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="caption">{(deal.dnetPercentage ?? 0).toFixed(2)}%</Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="caption">{deal.term} mo</Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="caption" fontWeight={600}>
                      {(deal.subsidyPercentage ?? 0).toFixed(2)}%
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="caption" fontWeight={700} color="success.dark">
                      {formatCurrency(deal.approvedSubsidyAmount ?? 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>

          <TableFooter>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell colSpan={4}>
                <Typography variant="caption" fontWeight={700}>
                  PAGE TOTAL ({deals.length} of {total})
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="caption" fontWeight={700}>
                  {formatCurrency(pageFinanceTotal)}
                </Typography>
              </TableCell>
              <TableCell colSpan={4} />
            </TableRow>
            <TableRow>
              <TablePagination
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
                onPageChange={(_, newPage) => onPageChange(newPage)}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};
